import { useSortable } from '@dnd-kit/sortable';
import type { MantineTheme } from '@mantine/core';
import { Box, createStyles, Group, Stack } from '@mantine/core';
import { motion } from 'framer-motion';
import { type WidgetSize } from '../../../interfaces';
import { getAnimation, getTransition, getWidgetSpan } from '../utils/utils';

const useStyles = createStyles(
	(
		theme: MantineTheme,
		params: {
			size: WidgetSize;
			isDragging: boolean;
		}
	) => ({
		container: {
			minHeight: 364,
			minWidth: 364,
			borderRadius: theme.radius.md,
			borderWidth: 1,
			borderStyle: 'solid',
			borderColor: theme.other.getColor('fill/secondary/default'),
			gridColumn: getWidgetSpan(params.size),
			backgroundColor: theme.other.getColor('surface/primary/default'),
			boxShadow: params.isDragging ? theme.shadows.lg : undefined,
			cursor: 'default!important',
			'& .mantine-Stack-root': {
				overflowX: 'hidden',
			},
		},
		header: {
			cursor: params.isDragging ? 'grabbing' : 'grab',
			flex: '1 1 auto',
			minWidth: 24,
			maxWidth: '100%',
			padding: `${theme.spacing.sm} ${theme.spacing.md} 0 ${theme.spacing.md}`,
			alignItems: 'flex-start',
		},
		titleStack: {
			minWidth: 0,
			maxWidth: '100%',
			flex: '1 1 auto',
		},
	})
);

interface WidgetWrapperProps {
	metricId: string;
	size: WidgetSize;
	left: React.ReactNode;
	right: React.ReactNode;
	content: React.ReactNode;
}

export function WidgetWrapper({
	metricId,
	size,
	left,
	right,
	content,
}: WidgetWrapperProps) {
	const {
		attributes,
		setActivatorNodeRef,
		setNodeRef,
		transform,
		isDragging,
		listeners,
	} = useSortable({
		id: metricId,
		transition: null,
	});

	const { classes } = useStyles({
		size: size,
		isDragging,
	});

	return (
		<motion.div
			className={classes.container}
			ref={setNodeRef}
			layoutId={metricId}
			{...{
				...attributes,
				animate: getAnimation(transform, isDragging),
				transition: getTransition(isDragging),
			}}
		>
			<Group noWrap position="apart" className={classes.header}>
				<Stack
					spacing={0}
					className={classes.titleStack}
					ref={setActivatorNodeRef}
					{...listeners}
				>
					{left}
				</Stack>
				<Group noWrap spacing={0}>
					{right}
				</Group>
			</Group>
			<Box>{content}</Box>
		</motion.div>
	);
}
